import type {UpdateCarrierOption} from '@/types/CarrierOption';
import type {CarrierId, CarrierName, CarrierOption, ValueOf} from '@myparcel-frontend/types';
import type {Ref} from 'vue';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {storeToRefs} from 'pinia';
import {computed, ref} from 'vue';
import useAccount from '@/composables/useAccount/useAccount';
import useShop from '@/composables/useShop/useShop';
import {useCarrierStore} from '@/store/carrierStore/carrierStore';
import {ContractType} from '@/types/CarrierOption';
import {hasMainContract} from '@/utils/carriers';

const inFlightRequests = new Map<string, Promise<void>>();

/**
 * Callers ask for carrier options once per shipment row, all in the same tick. The store is still empty at that
 * moment, so every row would start its own request. Sharing the running promise turns that burst into one call.
 */
const shareRunningRequest = (key: string, startRequest: () => Promise<void>): Promise<void> => {
  const running = inFlightRequests.get(key) ?? startRequest().finally(() => inFlightRequests.delete(key));

  inFlightRequests.set(key, running);

  return running;
};

export default function useCarriers() {
  const status = ref<ValueOf<typeof Status>>(Status.IDLE);
  const {getAccount} = useAccount();
  const {currentShop} = useShop();
  const carrierStore = useCarrierStore();
  const {accountCarriers} = storeToRefs(carrierStore);
  const {sdk} = useSdk();

  const fetchAccountCarriers = async (): Promise<CarrierOption[]> => {
    const {sdk} = useSdk();
    const mainAccount = await getAccount();
    return sdk.getCarrierOptions({
      path: {account_id: mainAccount.id},
    });
  };

  const getAccountCarriers = async (): Promise<Readonly<Ref<CarrierOption[]>>> => {
    if (!carrierStore.getAccountCarriers().value.length) {
      await shareRunningRequest('accountCarriers', updateAccountCarrierOptions);
    }

    return carrierStore.getAccountCarriers();
  };

  const updateAccountCarrierOptions = async (): Promise<void> => {
    const carrierOptions = await fetchAccountCarriers();
    carrierStore.setAccountCarriers(carrierOptions);
  };

  const postAccountCarrierOptions = async (carrierOption: UpdateCarrierOption): Promise<void> => {
    const {sdk} = useSdk();
    const mainAccount = await getAccount();

    await sdk.postCarrierOptions({
      path: {
        account_id: mainAccount.id,
      },
      body: [carrierOption],
    });

    await updateAccountCarrierOptions();
  };

  const deleteAccountCarrierOption = async (contractId: number): Promise<void> => {
    const {sdk} = useSdk();
    const mainAccount = await getAccount();

    try {
      await sdk.deleteCarrierOption({
        path: {
          contract_id: contractId,
          account_id: mainAccount.id,
        },
      });
    } catch (error) {
      throw new Error('Could not delete carrier option');
    }

    await updateAccountCarrierOptions();
  };

  const allContractsInStore = (shopId: number = currentShop.value.id): CarrierOption[] => {
    const accountContracts = carrierStore.getAccountCarriers().value;
    const shopContracts = carrierStore.getShopCarriers(shopId);
    return [...accountContracts, ...shopContracts];
  };

  const isCustomContract = (contractId: number): boolean => {
    const carrier = allContractsInStore().find((carrier) => carrier.id === contractId);

    return carrier?.type === ContractType.CUSTOM;
  };

  const isMainContract = (contractId: number): boolean => {
    const carrier = allContractsInStore().find((carrier) => carrier.id === contractId);
    return hasMainContract(carrier);
  };

  const getAccountContractIdForCarrierId = (carrierId: ValueOf<typeof CarrierId>): number | undefined => {
    const carrier = carrierStore.getAccountCarriers().value.find((carrier) => carrier.carrier_id === carrierId);
    return carrier?.id;
  };

  const getCarrierIdForContractId = (contractId: number): ValueOf<typeof CarrierId> | undefined => {
    const carrier = allContractsInStore().find((carrier) => carrier.id === contractId);
    return carrier?.carrier_id;
  };

  const getCarrierName = (contractId: number): ValueOf<typeof CarrierName> | undefined => {
    const carrier = allContractsInStore().find((carrier) => carrier.id === contractId);
    return carrier?.carrier.name;
  };

  const mainAccountCarrierOptions: Readonly<Ref<CarrierOption[]>> = computed(() => {
    return carrierStore.getAccountCarriers().value.filter((carrier) => carrier.type === ContractType.MAIN);
  });

  const customAccountCarrierOptions: Readonly<Ref<CarrierOption[]>> = computed(() => {
    return carrierStore.getAccountCarriers().value.filter((carrier) => carrier.type === ContractType.CUSTOM);
  });

  const updateAccountCarrierOption = async (oldValue: CarrierOption, newValue: UpdateCarrierOption): Promise<void> => {
    await deleteAccountCarrierOption(oldValue.id);

    let updateSuccess = true;

    try {
      await postAccountCarrierOptions(newValue);
    } catch (error) {
      updateSuccess = false;
    }

    if (updateSuccess) {
      return;
    }

    await postAccountCarrierOptions({
      carrier_id: oldValue.carrier_id,
      username: oldValue.username,
      password: oldValue.password,
      options: oldValue.options,
    });

    throw new Error('Could not update carrier option');
  };

  const fetchShopCarrierOptions = async (): Promise<void> => {
    status.value = Status.PENDING;

    let response;

    try {
      response = await sdk.getShopCarrierOptions({
        path: {
          shop_id: currentShop.value.id,
        },
      });
      status.value = Status.RESOLVED;
    } catch (err) {
      status.value = Status.REJECTED;
      throw err;
    }

    carrierStore.setShopCarriers(currentShop.value.id, response);
  };

  const getShopCarrierOptions = async (): Promise<Ref<CarrierOption[]>> => {
    const shopId = currentShop.value.id;

    // A shop with genuinely zero carrier options stays indistinguishable from "not loaded yet" and keeps refetching.
    if (carrierStore.getShopCarriers(shopId).length === 0) {
      await shareRunningRequest(`shopCarriers:${shopId}`, fetchShopCarrierOptions);
    }

    return computed(() => carrierStore.getShopCarriers(currentShop.value.id));
  };

  const postShopCarrierOption = async (carrierOption: UpdateCarrierOption): Promise<void> => {
    try {
      await sdk.postShopCarrierOptions({
        path: {
          shop_id: currentShop.value.id,
        },
        body: [carrierOption],
      });
    } catch (err) {
      status.value = Status.REJECTED;
      throw err;
    }

    await fetchShopCarrierOptions();
  };

  const updateShopCarrierOption = async (oldValue: CarrierOption, newValue: UpdateCarrierOption): Promise<void> => {
    try {
      await deleteShopCarrierOption(oldValue.id);
    } catch (error) {
      throw new Error('Could not delete carrier option');
    }

    let updateSuccess = true;

    try {
      await postShopCarrierOption(newValue);
    } catch (error) {
      updateSuccess = false;
    }

    if (updateSuccess) {
      return;
    }

    // Revert to old values
    await postShopCarrierOption({
      carrier_id: oldValue.carrier_id,
      username: oldValue.username,
      password: oldValue.password,
      options: oldValue.options,
    });

    throw new Error('Could not update carrier option');
  };

  const setShopCarrierOptionEnabledState = async (carrierOption: Partial<CarrierOption>): Promise<void> => {
    try {
      await sdk.putShopCarrierOptions({
        path: {
          shop_id: currentShop.value.id,
        },
        body: [carrierOption],
      });

      carrierStore.updateShopCarrier(currentShop.value.id, carrierOption);
    } catch (err) {
      status.value = Status.REJECTED;
      throw err;
    }
  };

  const deleteShopCarrierOption = async (contractId: number): Promise<void> => {
    try {
      await sdk.deleteShopCarrierOption({
        path: {
          shop_id: currentShop.value.id,
          id: contractId,
        },
      });
    } catch (err) {
      status.value = Status.REJECTED;
      throw err;
    }

    carrierStore.deleteShopCarrier(currentShop.value.id, contractId);
  };

  const customShopCarrierOptions: Readonly<Ref<CarrierOption[]>> = computed(() => {
    return carrierStore.getShopCarriers(currentShop.value.id).filter((carrier) => carrier.type === ContractType.CUSTOM);
  });

  /**
   * All custom shop contracts that are not also account contracts.
   * The user can have a custom contract for the same carrier on shop and account level.
   * So we filter out the account contracts.
   */
  const uniqueShopContracts: Readonly<Ref<CarrierOption[]>> = computed(() =>
    customShopCarrierOptions.value.filter(
      (shopCarrierOption: CarrierOption) =>
        !customAccountCarrierOptions.value.find(
          (accountCarrierOption) => accountCarrierOption.id === shopCarrierOption.id,
        ),
    ),
  );

  /**
   * All custom account contracts of the shop.
   * The user can have a custom contract for the same carrier on shop and account level.
   * So we filter out the shop contracts.
   */
  const shopAccountContracts: Readonly<Ref<CarrierOption[]>> = computed(() =>
    customShopCarrierOptions.value.filter(
      (accountCarrierOption: CarrierOption) =>
        !uniqueShopContracts.value.find((shopCarrierOption) => shopCarrierOption.id === accountCarrierOption.id),
    ),
  );

  return {
    allContractsInStore,
    accountCarriers,
    getAccountCarriers,
    updateAccountCarrierOptions,
    postAccountCarrierOptions,
    deleteAccountCarrierOption,
    isCustomContract,
    isMainContract,
    getCarrierName,
    getAccountContractIdForCarrierId,
    getCarrierIdForContractId,
    customAccountCarrierOptions,
    mainAccountCarrierOptions,
    updateAccountCarrierOption,
    fetchShopCarrierOptions,
    getShopCarrierOptions,
    postShopCarrierOption,
    updateShopCarrierOption,
    setShopCarrierOptionEnabledState,
    deleteShopCarrierOption,
    customShopCarrierOptions,
    uniqueShopContracts,
    shopAccountContracts,
  };
}
